/** Server-owned access and saved choice for the bundled Web visualization plugin. */
export interface VisualizeEntitlement {
  featureId: 'visualize';
  title: string;
  enabled: boolean;
  allowed: boolean;
  requiredPlans: string[];
  requiredPlanNames?: string[];
}

export const VISUALIZE_PLUGIN_ID = 'visualize@reados-bundled';
export const VISUALIZE_PLUGIN_VERSION = '1.0.41';
/** Stock app-server qualifies plugin skills as plugin-name:skill-name. */
export const VISUALIZE_SKILL_NAME = 'visualize:visualize';

export interface DocumentVisualizationSource {fileId:string;contentRevision:string}
export interface DocumentFollowUpSource {documentFileId:string}
/** Values exposed by a visible diagram. privateContent never enters model context. */
export interface ReaderVisualizationState {
  reference:string;
  ordinal:number;
  title?:string;
  contentRevision:string;
  documentRevision:string;
  modelContent:unknown;
  /** Authoritative resource location, added by the server after source validation. */
  sandboxPath?:string;
}
export interface VisualizationReference {
  path:string;title:string;mode:'inline'|'wide';start:number;end:number;
}
export const VISUALIZATION_STATE_LIMIT=16_384;
export const VISUALIZATION_CONTEXT_LIMIT=6;
export const DOCUMENT_VISUALIZATION_ASSET_ROOT='/workspace/.reados/document-visualizations';

export function visualizationReferencePath(value:unknown,{allowRelative=false}:{allowRelative?:boolean}={}):string|null {
  if(typeof value!=='string'||!value||value.length>8192||/[\\\u0000-\u001f\u007f]/.test(value)
    ||!/^[a-z0-9]+(?:-[a-z0-9]+)*\.html$/.test(value.split('/').at(-1)||''))return null;
  if(value.startsWith('/workspace/'))return value.slice(1).split('/').some(part=>!part||part==='.'||part==='..')?null:value;
  if(!allowRelative||value.startsWith('/')||value.split('/').some(part=>!part)||/^[a-z][a-z0-9+.-]*:/i.test(value))return null;
  return value;
}

/** Resolve literal filesystem names, without URL-decoding percent sequences. */
export function resolveDocumentVisualizationPath(reference:unknown,documentSandboxPath:string):string|null {
  const raw=visualizationReferencePath(reference,{allowRelative:true});if(!raw)return null;
  if(raw.startsWith('/workspace/'))return raw;
  if(!documentSandboxPath.startsWith('/workspace/')||/[\\\u0000-\u001f\u007f]/.test(documentSandboxPath))return null;
  const parts=documentSandboxPath.slice(1).split('/');
  if(parts.some(part=>!part||part==='.'||part==='..'))return null;
  parts.pop();
  for(const part of raw.split('/')){
    if(part==='.')continue;
    if(part==='..'){if(parts.length<=1)return null;parts.pop();}
    else parts.push(part);
  }
  return visualizationReferencePath('/'+parts.join('/'));
}

interface ParsedVisualizationReference extends VisualizationReference {jsonStart:number;jsonEnd:number}
function scanVisualizationReferences(source:string,options:{allowRelative?:boolean}):ParsedVisualizationReference[]{
  const found:ParsedVisualizationReference[]=[];let cursor=0,fence='',ticks=0;
  const accept=(start:number,end:number,jsonStart:number,jsonEnd:number)=>{
    try{const value=JSON.parse(source.slice(jsonStart,jsonEnd)),path=visualizationReferencePath(value?.path,options);
      if(path&&(value.mode==null||value.mode==='wide')&&(value.type==null||value.type==='inline'))found.push({path,title:typeof value.title==='string'?value.title:'',mode:value.mode==='wide'?'wide':'inline',start,end,jsonStart,jsonEnd});
    }catch{}
  };
  while(cursor<source.length){
    if(cursor===0||source[cursor-1]==='\n'){
      const line=source.slice(cursor).match(/^ {0,3}(`{3,}|~{3,})[^\n]*(?:\n|$)/);
      if(line&&(!fence||line[1][0]===fence[0]&&line[1].length>=fence.length)){fence=fence?'':line[1];cursor+=line[0].length;continue;}
    }
    if(!fence&&source[cursor]==='`'&&source[cursor-1]!=='\\'){
      const value=source.slice(cursor).match(/^`+/)![0];if(!ticks)ticks=value.length;else if(ticks===value.length)ticks=0;cursor+=value.length;continue;
    }
    if(!fence&&!ticks&&source[cursor-1]!=='\\'){
      const open='visualize';
      if(source.startsWith(open,cursor)){const end=source.indexOf('',cursor+open.length);if(end<0)break;accept(cursor,end+1,cursor+open.length,end);cursor=end+1;continue;}
      const legacy='::codex-inline-vis';
      if(source.startsWith(legacy+'{',cursor)){const end=source.indexOf('\n',cursor),stop=end<0?source.length:end;accept(cursor,stop,cursor+legacy.length,stop);cursor=stop;continue;}
    }
    cursor++;
  }
  return found;
}

/** The browser and publication pipeline recognize the same unfenced references. */
export function visualizationReferences(source:string,options:{allowRelative?:boolean}={}):VisualizationReference[]{
  return scanVisualizationReferences(source,options).map(({jsonStart:_,jsonEnd:__,...reference})=>reference);
}
export function rewriteVisualizationReferences(source:string,replacements:ReadonlyMap<string,string>):string {
  const references=scanVisualizationReferences(source,{allowRelative:true});let result='',cursor=0;
  for(const reference of references){
    const replacement=replacements.get(reference.path);if(replacement===undefined)continue;
    if(!visualizationReferencePath(replacement,{allowRelative:true}))throw new Error('Invalid visualization replacement path');
    const original=JSON.parse(source.slice(reference.jsonStart,reference.jsonEnd));
    result+=source.slice(cursor,reference.jsonStart)+JSON.stringify({...original,path:replacement});cursor=reference.jsonEnd;
  }
  return result+source.slice(cursor);
}

/** JSON data only; returns a detached value suitable for private storage/context. */
export function boundedVisualizationValue(value:unknown,maxBytes=VISUALIZATION_STATE_LIMIT):unknown {
  const encoded=JSON.stringify(value,(_key,item)=>{if(typeof item==='number'&&!Number.isFinite(item))throw new Error('Invalid visualization number');return item;});
  if(encoded===undefined||new TextEncoder().encode(encoded).length>maxBytes)throw new Error('Visualization state is too large');
  return JSON.parse(encoded);
}
